import { useSearchParams } from 'react-router-dom';
import LivenessSession from '../components/Liveness';
import { useState, useEffect } from 'react';
import { Button } from '../shadcn/Button';
import firestore from '../firestore';

export default function LivenessDetection({
	config
}: {
	config?: {
		isNewUser?: boolean;
		userIdentificationIdUrl: string;
		livenessSessionId?: string;
		livenessDocumentId?: string;
		userIdentificationNumber?: string;
		embeddedMode?: boolean;
		onReady?: () => void;
	};
}) {
	const [success, setSuccess] = useState(false);
	const [linkExpired, setLinkExpired] = useState(false);
	const [error, setError] = useState(false);
	const [livenessRetryKey, setLivenessRetryKey] = useState(0);
	const [validSessionId, setValidSessionId] = useState<boolean | null>(null);
	const [documentLoading, setDocumentLoading] = useState(true);

	const [searchParams] = useSearchParams();

	const theme = searchParams.get('theme');
	const documentId = config?.livenessDocumentId || searchParams.get('document_id');
	const livenessSessionId = config?.livenessSessionId || searchParams.get('liveness_session_id');
	const userIdentificationNumber = config?.userIdentificationNumber || searchParams.get('user_identification_number');
	const identificationIdUrl = config?.userIdentificationIdUrl || searchParams.get('identification_id_url');
	const isNewUser =
		config?.isNewUser !== undefined ? config.isNewUser : searchParams.get('is_new_user') === 'true' ? true : false;
	const embeddedMode = config?.embeddedMode || searchParams.get('embedded_mode') === 'true' ? true : false;
	const origin = searchParams.get('origin');

	// Decode the URL-encoded parameters
	const decodedSessionId = livenessSessionId ? decodeURIComponent(livenessSessionId) : null;
	const decodedDocumentId = documentId ? decodeURIComponent(documentId) : null;
	const decodedUserIdentificationNumber = userIdentificationNumber ? decodeURIComponent(userIdentificationNumber) : null;
	let decodedIdentificationIdUrl = identificationIdUrl ? decodeURIComponent(identificationIdUrl) : null;
	if (decodedIdentificationIdUrl && decodedIdentificationIdUrl.includes('/o/')) {
		const oIndex = decodedIdentificationIdUrl.indexOf('/o/');
		const afterO = decodedIdentificationIdUrl.substring(oIndex + 3);
		if (afterO.includes('/')) {
			const firstSlashIndex = afterO.indexOf('/');
			decodedIdentificationIdUrl = decodedIdentificationIdUrl.substring(0, oIndex + 3 + firstSlashIndex) + '%2F' + afterO.substring(firstSlashIndex + 1);
		}
	}

	const [startScreen, setStartScreen] = useState(embeddedMode ? false : true);

	// Fetch document and validate session ID on mount
	
	useEffect(() => {
		if (!decodedDocumentId) {
			setDocumentLoading(false);
			return;
		}

		firestore
			.collection('LivenessSessions')
			.doc(decodedDocumentId)
			.get()
			.then((doc) => {
				if (doc.exists) {
					const data = doc.data();
					const documentSessionId = data?.liveness_session_id;

					// Only consider sessionId valid if it matches the document's session ID
					const isValidSession = decodedSessionId && documentSessionId && decodedSessionId === documentSessionId;
					setValidSessionId(isValidSession || false);
				} else {
					setValidSessionId(false);
				}
			})
			.catch((error) => {
				console.error('Error fetching document:', error);
				setValidSessionId(false);
			})
			.finally(() => {
				setDocumentLoading(false);
			});
	}, [decodedDocumentId, decodedSessionId]);

	if (documentLoading) {
		return (
			<div
				className={embeddedMode ? 'contents' : 'flex h-[100lvh] w-full items-center justify-center'}
				style={
					theme
						? {
								backgroundColor: theme === 'dark' ? 'hsla(222, 47%, 11%, 1)' : 'hsla(216, 56%, 98%, 1)'
							}
						: {}
				}
			>
				<div className="max-w-[95%] text-center">
					<p className="text-neutral-300">Validando sesión...</p>
				</div>
			</div>
		);
	} else if (!decodedSessionId || !decodedDocumentId || !decodedUserIdentificationNumber) {
		return (
			<div
				className={embeddedMode ? 'contents' : 'flex h-[100lvh] w-full items-center justify-center'}
				style={
					theme
						? {
								backgroundColor: theme === 'dark' ? 'hsla(222, 47%, 11%, 1)' : 'hsla(216, 56%, 98%, 1)'
							}
						: {}
				}
			>
				<div className="max-w-[95%] text-center">
					<h2 className="mb-2 text-xl font-semibold text-red-600">Error</h2>
					<p className="text-neutral-300">Faltan parámetros requeridos para la verificación biométrica.</p>
				</div>
			</div>
		);
	} else if (linkExpired) {
		return (
			<div
				className={embeddedMode ? 'contents' : 'flex h-[100lvh] w-full items-center justify-center'}
				style={
					theme
						? {
								backgroundColor: theme === 'dark' ? 'hsla(222, 47%, 11%, 1)' : 'hsla(216, 56%, 98%, 1)'
							}
						: {}
				}
			>
				<div className="max-w-[95%] text-center">
					<h2 className="mb-2 text-xl font-semibold text-red-600">Error</h2>
					<p className="text-neutral-300">
						{embeddedMode ? 'El proceso ha expirado. Debe comenzar de nuevo.' : 'Este enlace ha expirado. Solicite uno nuevo.'}
					</p>
				</div>
			</div>
		);
	} else if (error) {
		return (
			<div
				className={embeddedMode ? 'contents' : 'flex h-[100lvh] w-full items-center justify-center'}
				style={
					theme
						? {
								backgroundColor: theme === 'dark' ? 'hsla(222, 47%, 11%, 1)' : 'hsla(216, 56%, 98%, 1)'
							}
						: {}
				}
			>
				<div className={`${embeddedMode ? 'w-full' : 'w-full max-w-[95%]'} text-center`}>
					<div className="!my-8 space-y-4">
						{/* Icon */}
						<div className="mb-6 flex justify-center">
							<div className="relative">
								<div className="flex size-14 items-center justify-center rounded-2xl bg-gradient-to-br from-red-500 to-red-600 shadow-lg shadow-red-500/25">
									<svg className="h-10 w-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5">
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
										/>
									</svg>
								</div>
								<div className="absolute -inset-1 -z-10 rounded-2xl bg-gradient-to-br from-red-500/30 to-red-600/30 blur-sm"></div>
							</div>
						</div>
						<h2
							className={embeddedMode ? 'hidden' : 'text-xl font-semibold text-red-600'}
							style={
								theme
									? {
											color: theme === 'dark' ? '#ef4444' : '#dc2626'
										}
									: {}
							}
						>
							Error en la verificación
						</h2>
						<p
							className="mx-auto max-w-[280px] text-neutral-300"
							style={
								theme
									? {
											color: theme === 'dark' ? 'hsla(216, 56%, 85%, 1)' : 'hsla(222, 47%, 25%, 1)'
										}
									: {}
							}
						>
							La verificación falló. Por favor vuelva a intentarlo.
						</p>
						{/* Disclaimer */}
						<div
							className="mx-auto max-w-[320px] rounded-lg border border-red-200 bg-red-50 p-3"
							style={
								theme
									? {
											backgroundColor: theme === 'dark' ? 'hsla(0, 47%, 15%, 1)' : 'hsla(0, 56%, 95%, 1)',
											borderColor: theme === 'dark' ? 'hsla(0, 47%, 25%, 1)' : 'hsla(0, 56%, 85%, 1)'
										}
									: {}
							}
						>
							<div className="flex items-start gap-2">
								<svg
									className="mt-0.5 h-3 w-3 flex-shrink-0 text-red-600"
									style={
										theme
											? {
													color: theme === 'dark' ? 'hsla(0, 56%, 75%, 1)' : 'hsla(0, 47%, 35%, 1)'
												}
											: {}
									}
									fill="currentColor"
									viewBox="0 0 20 20"
								>
									<path
										fillRule="evenodd"
										d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
										clipRule="evenodd"
									/>
								</svg>
								<p
									className="text-xs text-red-700"
									style={
										theme
											? {
													color: theme === 'dark' ? 'hsla(0, 56%, 75%, 1)' : 'hsla(0, 47%, 35%, 1)'
												}
											: {}
									}
								>
									Hubo un problema durante la verificación. <span className="font-bold">Intente nuevamente</span>
								</p>
							</div>
						</div>
					</div>
					<Button
						className={`max-w-max rounded-full bg-[hsla(192,91%,36%,1)] font-semibold tracking-tight${embeddedMode ? '!-mb-6' : ''}`}
						variant="primary"
						onClick={() => {
							setSuccess(false);
							setError(false);
							setLivenessRetryKey((prev) => prev + 1);
						}}
					>
						Reintentar
					</Button>
				</div>
			</div>
		);
	} else if (success) {
		return (
			<div
				className={embeddedMode ? 'hidden' : 'flex h-[100lvh] w-full items-center justify-center'}
				style={
					theme
						? {
								backgroundColor: theme === 'dark' ? 'hsla(222, 47%, 11%, 1)' : 'hsla(216, 56%, 98%, 1)'
							}
						: {}
				}
			>
				<div className="max-w-[95%] text-center">
					<h2 className="mb-2 text-xl font-semibold text-green-600">Verificación exitosa</h2>
					<p className="text-neutral-300">Puede regresar al proceso de {isNewUser ? 'registro' : 'venta'}.</p>
				</div>
			</div>
		);
	} else if (startScreen) {
		// Different start screen for app origin
		if (origin === 'app') {
			return (
				<div
					className={embeddedMode ? 'contents' : 'flex h-[100lvh] w-full items-center justify-center'}
					style={
						theme
							? {
									backgroundColor: theme === 'dark' ? 'hsla(222, 47%, 11%, 1)' : 'hsla(216, 56%, 98%, 1)'
								}
							: {}
					}
				>
					<div className={`${embeddedMode ? 'w-full' : 'w-[calc(100%-2rem)] max-w-[340px]'} text-center`}>
						<div className="space-y-6">
							{/* Modern Icon */}
							<div className="mb-4 flex justify-center">
								<div className="relative">
									<div className="flex size-24 items-center justify-center rounded-full p-4 bg-[rgb(6,182,212)]/10">
										<svg
											xmlns="http://www.w3.org/2000/svg"
											width="24"
											height="24"
											className="size-[4.25rem] text-[rgb(6,182,212)]"
											viewBox="0 0 24 24"
										>
											<g fill="none">
												<path
													stroke="currentColor"
													stroke-linecap="round"
													stroke-width="1.75"
													d="M9 16c.85.63 1.885 1 3 1s2.15-.37 3-1"
												/>
												<ellipse cx="15" cy="10.5" fill="currentColor" rx="1" ry="1.75" />
												<ellipse cx="9" cy="10.5" fill="currentColor" rx="1" ry="1.75" />
												<path
													stroke="currentColor"
													stroke-linecap="round"
													stroke-width="1.75"
													d="M22 14c0 3.771 0 5.657-1.172 6.828S17.771 22 14 22m-4 0c-3.771 0-5.657 0-6.828-1.172S2 17.771 2 14m8-12C6.229 2 4.343 2 3.172 3.172S2 6.229 2 10m12-8c3.771 0 5.657 0 6.828 1.172S22 6.229 22 10"
												/>
											</g>
										</svg>
									</div>
								</div>
							</div>

							{/* Title */}
							<div className="space-y-0.5">
								<h2
									className={embeddedMode ? 'hidden' : 'text-left text-[1.725rem] leading-[1.15] tracking-tight font-semibold'}
									style={
										theme
											? {
													color: theme === 'dark' ? 'white' : 'black'
												}
											: {}
									}
								>
									Ahora hay que escanear tu rostro
								</h2>
							</div>

							{/* Instructions */}
							<div className="mx-auto max-w-[320px] space-y-3">
								<div className="flex items-start gap-3 text-left">
									<div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[hsla(192,91%,36%,0.1)]">
										<span className="text-xs font-semibold text-[hsla(192,91%,36%,1)]">1</span>
									</div>
									<p
										className="text-sm"
										style={
											theme
												? {
														color: theme === 'dark' ? 'hsla(216, 56%, 85%, 1)' : 'hsla(222, 47%, 25%, 1)'
													}
												: {}
										}
									>
										Ubícate en un lugar <span className="font-semibold text-[hsla(192,91%,36%,1)]">bien iluminado</span>
									</p>
								</div>
								<div className="flex items-start gap-3 text-left">
									<div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[hsla(192,91%,36%,0.1)]">
										<span className="text-xs font-semibold text-[hsla(192,91%,36%,1)]">2</span>
									</div>
									<p
										className="text-sm"
										style={
											theme
												? {
														color: theme === 'dark' ? 'hsla(216, 56%, 85%, 1)' : 'hsla(222, 47%, 25%, 1)'
													}
												: {}
										}
									>
										Si usas lentes, <span className="font-semibold text-[hsla(192,91%,36%,1)]">quítatelos</span> antes de comenzar
									</p>
								</div>
								<div className="flex items-start gap-3 text-left">
									<div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[hsla(192,91%,36%,0.1)]">
										<span className="text-xs font-semibold text-[hsla(192,91%,36%,1)]">3</span>
									</div>
									<p
										className="text-sm"
										style={
											theme
												? {
														color: theme === 'dark' ? 'hsla(216, 56%, 85%, 1)' : 'hsla(222, 47%, 25%, 1)'
													}
												: {}
										}
									>
										Mantén tu rostro <span className="font-semibold text-[hsla(192,91%,36%,1)]">visible y centrado</span>
									</p>
								</div>
							</div>

							{/* Info Box */}
							<div className="mx-auto max-w-[340px] rounded-xl border border-[hsla(192,91%,36%,0.1)] bg-gradient-to-r from-[hsla(192,91%,36%,0.05)] to-[hsla(192,91%,36%,0.02)] p-4">
								<div className="flex items-start gap-3">
									<svg
										className="mt-0.5 h-4 w-4 flex-shrink-0 text-[hsla(192,91%,36%,1)]"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
										strokeWidth="1.5"
									>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
										/>
									</svg>
									<p
										className="text-left text-xs"
										style={
											theme
												? {
														color: theme === 'dark' ? 'hsla(216, 56%, 75%, 1)' : 'hsla(222, 47%, 35%, 1)'
													}
												: {}
										}
									>
										El proceso tomará solo unos segundos y verificará tu identidad de forma segura
									</p>
								</div>
							</div>
						</div>

						<Button
							className={`max-w-max rounded-full bg-[hsla(192,91%,36%,1)] px-8 py-3 mt-6 font-semibold tracking-tight text-sm${embeddedMode ? '!-mb-6' : ''}`}
							variant="primary"
							onClick={() => {
								setStartScreen(false);
							}}
						>
							Comenzar Verificación
						</Button>
					</div>
				</div>
			);
		} else {
			// Original start screen for non-app origins
			return (
				<div
					className={embeddedMode ? 'contents' : 'flex h-[100lvh] w-full items-center justify-center'}
					style={
						theme
							? {
									backgroundColor: theme === 'dark' ? 'hsla(222, 47%, 11%, 1)' : 'hsla(216, 56%, 98%, 1)'
								}
							: {}
					}
				>
					<div className={`${embeddedMode ? 'w-full' : 'w-full max-w-[95%]'} text-center`}>
						<div className="!my-8 space-y-4">
							{/* Icon */}
							<div className="mb-6 flex justify-center">
								<div className="relative">
									<div className="flex size-14 items-center justify-center rounded-2xl bg-gradient-to-br from-[hsla(192,91%,36%,1)] to-[hsla(192,91%,28%,1)] shadow-lg shadow-[hsla(192,91%,36%,0.25)]">
										<svg className="h-10 w-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="1.5">
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.623 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z"
											/>
										</svg>
									</div>
									<div className="absolute -inset-1 -z-10 rounded-2xl bg-gradient-to-br from-[hsla(192,91%,36%,0.3)] to-[hsla(192,91%,28%,0.3)] blur-sm"></div>
								</div>
							</div>
							<h2
								className={embeddedMode ? 'hidden' : 'text-xl font-semibold text-[hsla(192,91%,36%,1)]'}
								style={
									theme
										? {
												color: theme === 'dark' ? 'white' : 'black'
											}
										: {}
								}
							>
								Verificación Biométrica
							</h2>
							<p
								className="mx-auto max-w-[280px] text-neutral-300"
								style={
									theme
										? {
												color: theme === 'dark' ? 'hsla(216, 56%, 85%, 1)' : 'hsla(222, 47%, 25%, 1)'
											}
										: {}
								}
							>
								Cuando esté listo, haga click en el botón para comenzar la verificación.
							</p>
							{/* Disclaimer */}
							<div
								className="mx-auto max-w-[320px] rounded-lg border border-blue-200 bg-blue-50 p-3"
								style={
									theme
										? {
												backgroundColor: theme === 'dark' ? 'hsla(222, 47%, 15%, 1)' : 'hsla(216, 56%, 95%, 1)',
												borderColor: theme === 'dark' ? 'hsla(222, 47%, 25%, 1)' : 'hsla(216, 56%, 85%, 1)'
											}
										: {}
								}
							>
								<div className="flex items-start gap-2">
									<svg
										className="mt-0.5 h-3 w-3 flex-shrink-0 text-blue-600"
										style={
											theme
												? {
														color: theme === 'dark' ? 'hsla(216, 56%, 75%, 1)' : 'hsla(222, 47%, 35%, 1)'
													}
												: {}
										}
										fill="currentColor"
										viewBox="0 0 20 20"
									>
										<path
											fillRule="evenodd"
											d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
											clipRule="evenodd"
										/>
									</svg>
									<p
										className="text-xs text-blue-700"
										style={
											theme
												? {
														color: theme === 'dark' ? 'hsla(216, 56%, 75%, 1)' : 'hsla(222, 47%, 35%, 1)'
													}
												: {}
										}
									>
										Esta verificación biométrica es <span className="font-bold">para crédito financiero con Krece</span>
									</p>
								</div>
							</div>
						</div>
						<Button
							className={`max-w-max rounded-full bg-[hsla(192,91%,36%,1)] font-semibold tracking-tight${embeddedMode ? '!-mb-6' : ''}`}
							variant="primary"
							onClick={() => {
								setStartScreen(false);
							}}
						>
							Comenzar Verificación
						</Button>
					</div>
				</div>
			);
		}
	} else {
		return (
			<div
				className={embeddedMode ? 'contents' : 'flex h-[100lvh] w-full items-center justify-center'}
				style={
					theme
						? {
								backgroundColor: theme === 'dark' ? 'hsla(222, 47%, 11%, 1)' : 'hsla(216, 56%, 98%, 1)'
							}
						: {}
				}
			>
				<div className={embeddedMode ? 'contents' : 'w-full md:w-1/2'}>
					<LivenessSession
						key={livenessRetryKey}
						isNewUser={isNewUser}
						userIdentificationIdUrl={decodedIdentificationIdUrl || undefined}
						livenessSessionId={livenessRetryKey === 0 && validSessionId ? decodedSessionId : undefined}
						livenessDocumentId={decodedDocumentId}
						userIdentificationNumber={decodedUserIdentificationNumber!}
						origin={origin || undefined}
						onSuccess={() => setSuccess(true)}
						onError={(error) => {
							if (error === 'link-expired') {
								setLinkExpired(true);
							} else {
								setError(true);
							}
						}}
						onReady={() => {
							if (config?.onReady) {
								config.onReady();
							} else {
								firestore
									.collection('LivenessSessions')
									.doc(decodedDocumentId)
									.update({
										processing: true
									})
									.catch((error) => {
										console.error(error);
									});
							}
						}}
					/>
				</div>
			</div>
		);
	}
}
